import { useCallback, useState } from "react";
import { putRequest, getRequest, postRequest, deleteRequest } from "lib/core/apiClient";
import { errorToast } from "lib/utils/toasters";
import { useLocale } from "lib/hooks/useLocale";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { mutate } from "swr";
import { analyticsEvents, analyticsTypes, apiURI, eventTypes } from "lib/utils/constants";
import {
  publishItemUpdate,
  publishCartUpdate,
  getItems,
  getBillingDetails,
  extractConflictingItemsData,
  appendParamsToItemsUrl,
  getItemLevelAddOnsForItemIndex,
} from "lib/utils/checkout";
import { AddOnLevelType, AddOnType, BillingType, ItemType, ShippingHandlesType } from "lib/types/checkout";
import useSendAnalyticsEvent from "./useAnalytics";
import { publishPostMessage, redirectUrl, sleep } from "lib/utils/helpers";
import { CartNotePayload } from "lib/types/cartNotes";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { checkIfHasServiceabilityError } from "./useCheckoutErrors";
import { getUpsellUrl } from "lib/utils/upsell";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

const MIN_ITEM_QUANTITY = 1;

export type ItemEditAction = "increment" | "decrement" | "delete" | "set";

export interface ItemEditOverridePayload {
  itemIds?: string[];
  quantity?: number;
  /** Explicit target quantity per line item id. Takes precedence over itemIds/quantity. */
  items?: Array<{ id: string; quantity: number }>;
}

export interface UseItemEditParams {
  item_id?: string;
  quantity?: number;
  availableQuantity?: number;
  isCartItem?: boolean;
  variant_id?: string;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  billing?: BillingType;
}

export interface UseItemEditReturn {
  handleItemEdit: (action: ItemEditAction, overridePayload?: ItemEditOverridePayload) => Promise<void>;
  isLoading: boolean;
  showFullScreenLoader: boolean;
  openRemoveItemDialog: boolean;
  setOpenRemoveItemDialog: React.Dispatch<React.SetStateAction<boolean>>;
  handleItemDeleteSubmit: (isLastItem: boolean, raiseOnRemoval?: boolean) => Promise<void>;
  handleAddOnEdit: (args: HandleEditAddOnReturn) => Promise<boolean>;
  handleVariantSwap: (newVariantId: string, newVariantAvailableQuantity: number) => Promise<void>;
  handleAddItem: (items: Array<{ variantId: string; quantity: number }>, openCart?: boolean) => Promise<void>;
  handleAddItemWithResult: (
    items: Array<{ variantId: string; quantity: number }>,
    openCart?: boolean,
  ) => Promise<boolean>;
  handleRemoveItem: (items: RemoveItemInput[]) => Promise<void>;
}

export interface RemoveItemInput {
  /** Variant id of the item to remove. Ignored when `index` is provided. */
  variantId?: string;
  /** Index of the line item in the current checkout items list. */
  index?: number;
  /** Quantity to remove. Removes the whole line item when omitted. */
  quantity?: number;
}

export interface HandleEditAddOnReturn {
  action: "ADD" | "DELETE";
  addOnLevel: AddOnLevelType;
  addOnId: string;
  addOnPrice?: number;
  refetch?: boolean;
  showFullScreenLoader?: boolean;
  isCartNote?: boolean;
  cartNotePayload?: CartNotePayload;
  /** Overrides the hook's own item_id - needed right after a variant swap, where
   * the old line (and this hook's bound item_id) no longer exists. */
  itemId?: string;
}

export type AddOnData = {
  addOnType: AddOnLevelType;
  addOnId: string;
  price: number;
  itemId?: string;
};

export const useItemEdit = ({
  item_id,
  quantity,
  availableQuantity,
  isCartItem,
  variant_id,
  billing,
  setIsUpdating,
}: UseItemEditParams = {}) => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();
  const {
    state: { checkoutId, checkoutView, redirectUrl: backUrl, checkoutItems, availableAddOns },
    actions: {
      updateCheckoutBasedOnCheckoutResponse,
      setShippingHandles,
      setCheckoutModal,
      setCheckoutItems,
      setCheckout,
      setBilling,
      setCombinedProductRemovalItems,
    },
  } = useCheckoutContext();

  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { merchant, checkoutMetadata, cartMetadata },
  } = useMerchantContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();

  const upsell = isCartItem ? cartMetadata?.uiAttributes?.upsell : checkoutMetadata?.uiAttributes?.upsell;
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [openRemoveItemDialog, setOpenRemoveItemDialog] = useState<boolean>(false);

  const handleSendDeleteAnalyticsEvent = (addOnData?: AddOnData) => {
    const itemToDelete = checkoutItems.find((item) => item.variant_id == variant_id);
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_REMOVED_FROM_CART_UI,
      eventType: "click",
      eventFor: [analyticsTypes.SF_ANALYTICS],
      metaData: {
        checkoutItems: itemToDelete ? [itemToDelete] : [],
        ...(addOnData && { addOnData }),
      },
    });
  };

  const handleItemDeleteSubmit = async (isLastItem: boolean, raiseOnRemoval: boolean = false) => {
    //handling redirect to store on FE since BE can't do it as of now
    if (isLastItem) {
      // last item removal handled on the checkout-ui - clearCartAndCose
      publishCartUpdate([]);
      await sleep(50);
      if (isCartItem) {
        if (cartMetadata.uiAttributes?.upsell?.clearUpsellItemsOnCartClose) {
          const payload = checkoutItems.map(({ item_id }) => item_id);
          await handleItemEdit("delete", { itemIds: payload });
        }
        const itemToDelete = checkoutItems.find((item) => item.variant_id == variant_id);
        if (!cartMetadata.uiAttributes?.upsell?.clearUpsellItemsOnCartClose) {
          // fires ITEM_REMOVED for every item being cleared (including this one) - don't double-report
          await sendAnalyticsEvent({
            eventName: analyticsEvents.ITEM_REMOVED,
            eventType: "click",
            eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
            context: "cart",
            metaData: {
              itemDetails: itemToDelete
                ? [
                    {
                      item_id: itemToDelete.item_id,
                      product_id: itemToDelete.product_id,
                      variant_id: itemToDelete.variant_id,
                      item_title: itemToDelete.item_title,
                      variant_title: itemToDelete.variant_title,
                      quantity: itemToDelete.quantity,
                      price: itemToDelete.price,
                      is_upsell: itemToDelete.is_upsell,
                    },
                  ]
                : [],
            },
          });
        }
        publishPostMessage(eventTypes.CLEAR_CART_AND_CLOSE, itemToDelete);
      } else {
        redirectUrl(`${backUrl}/?floRedirectStatus=oos`);
      }
      setOpenRemoveItemDialog(false);
      return;
    }
    try {
      await handleItemEdit("delete", { itemIds: [] }, raiseOnRemoval);
      handleSendDeleteAnalyticsEvent();
    } catch (e) {
      console.error(e);
    } finally {
      setOpenRemoveItemDialog(false);
    }
  };

  const handleMiscUpdates = (
    response: any,
    {
      addType,
    }: {
      addType: "ITEM" | "ADDON";
    },
  ) => {
    // Item mutations return item-level add-on eligibility recomputed against the new
    // item arrangement (eg. a variant swap), so it's safe to trust it here; addon
    // mutations don't necessarily recompute it, so those keep the existing values.
    updateCheckoutBasedOnCheckoutResponse(response, "MAIN", addType === "ITEM");
    const showShippingHandles = response?.metadata?.show_shipping_handle_selector ?? false;
    const shippingHandles = response?.metadata?.available_shipping_handles ?? [];
    setShippingHandles(shippingHandles as ShippingHandlesType);

    // Consolidated upsell mutation logic
    const shouldMutateUpsell =
      addType === "ITEM" && Boolean(upsell?.isEnabled) && (checkoutView === "PAYMENTS" || isCartItem);

    if (shouldMutateUpsell) {
      mutate(getUpsellUrl({ checkoutId }));
    }

    if (checkoutView === "PAYMENTS") {
      mutatePayment();
      mutate(`/checkout/${checkoutId}/rewards`);
    }

    const hasServiceabilityError = checkIfHasServiceabilityError(response.metadata.error_responses);
    if (
      shippingHandles.length > 0 &&
      Boolean(showShippingHandles) &&
      Boolean(checkoutView === "PAYMENTS" && initialCheckoutStep !== "PAYMENTS") &&
      !Boolean(hasServiceabilityError)
    ) {
      openPopup("SHIPPING_HANDLES", {});
      return;
    }
    mutate(`UPI_INTENT`);
  };

  const handleSendItemUpdatedEvent = (checkoutItems: ItemType[]) => {
    const currentItem = checkoutItems.find((item) => item.variant_id === variant_id);
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_CART_QUANTITY_UPDATED,
      eventType: "click",
      eventFor: [analyticsTypes.SF_ANALYTICS],
      metaData: {
        checkoutItems: currentItem ? [currentItem] : [],
      },
    });
  };
  /**
   * Single place that writes the checkout items and revalidates everything that
   * depends on them.
   */
  const putCheckoutItems = async (
    payload: { items: Array<{ id?: string; quantity?: number } | Record<string, any>> },
    raiseOnRemoval: boolean = false,
  ) => {
    const ITEMS_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/items`, {
      ...(raiseOnRemoval && { raise_on_removal: "false" }),
    });
    const response: any = await putRequest(
      ITEMS_PATH,
      payload,
      isAuthenticated ? apiURI.KRATOS_PRIVATE : apiURI.KRATOS_PUBLIC,
    );

    mutate(appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`));
    mutate(`/checkout/${checkoutId}/rewards`);
    mutate(`/checkout/${checkoutId}/other-offers`);

    return response;
  };

  const handleItemEdit = useCallback(
    async (
      action: ItemEditAction,
      overridePayload?: ItemEditOverridePayload,
      raiseOnRemoval: boolean = false,
    ) => {
      const getQuantity = () => {
        switch (action) {
          case "increment": {
            if (!quantity || !availableQuantity)
              throw new Error("Quantity or available quantity is not defined");
            let newQuantity = quantity;
            if (
              Boolean(quantity + 1 > availableQuantity) ||
              (merchant?.maxQuantityPerItem &&
                Boolean(merchant?.maxQuantityPerItem) &&
                quantity + 1 > merchant?.maxQuantityPerItem)
            )
              return;
            newQuantity++;
            return newQuantity;
          }
          case "decrement": {
            if (!quantity || !availableQuantity)
              throw new Error("Quantity or available quantity is not defined");
            let newQuantity = quantity;
            if (quantity === MIN_ITEM_QUANTITY) return;
            newQuantity--;
            return newQuantity;
          }
          case "set": {
            if (!quantity || !availableQuantity)
              throw new Error("Quantity or available quantity is not defined");
            const target = overridePayload?.quantity;
            if (!target || target === quantity) return;
            const maxQuantity = Math.min(
              availableQuantity,
              merchant?.maxQuantityPerItem ? merchant.maxQuantityPerItem : availableQuantity,
            );
            return Math.max(MIN_ITEM_QUANTITY, Math.min(target, maxQuantity));
          }
          case "delete": {
            setOpenRemoveItemDialog(false);
            setShowFullScreenLoader(true);
            return 0;
          }
        }
      };
      // Callers that already know the target quantity of each line item (eg. the
      // window `removeItem` action) bypass the closure based quantity maths.
      const explicitItems = overridePayload?.items;
      if (Boolean(explicitItems) && !Boolean(explicitItems?.length)) return;

      let payload: { items: Array<{ id?: string; quantity?: number }> };
      if (explicitItems?.length) {
        if (explicitItems.some(({ quantity }) => quantity === 0)) {
          setOpenRemoveItemDialog(false);
          setShowFullScreenLoader(true);
        }
        payload = { items: explicitItems };
      } else {
        const newQuantity = getQuantity();

        // no-op actions (already at bound, unchanged target) must not fire a PUT with an undefined quantity
        if (newQuantity === undefined) return;
        payload = { items: [{ id: item_id, quantity: newQuantity }] };
        if (overridePayload?.itemIds?.length) {
          payload.items = overridePayload.itemIds.map((id) => ({ id, quantity: newQuantity }));
        }
      }

      try {
        setIsLoading(true);
        setIsUpdating?.(true);
        const response: any = await putCheckoutItems(payload, raiseOnRemoval);
        if (action === "delete") {
          const removedItemIds = overridePayload?.itemIds?.length
            ? overridePayload.itemIds
            : item_id
            ? [item_id]
            : [];
          const removedItems = checkoutItems.filter((item) => removedItemIds.includes(item.item_id));
          sendAnalyticsEvent({
            eventName: analyticsEvents.ITEM_REMOVED,
            eventType: "click",
            eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
            context: isCartItem ? "cart" : "checkout",
            metaData: {
              itemDetails: removedItems.map((item) => ({
                item_id: item.item_id,
                product_id: item.product_id,
                variant_id: item.variant_id,
                item_title: item.item_title,
                variant_title: item.variant_title,
                quantity: item.quantity,
                price: item.price,
                is_upsell: item.is_upsell,
              })),
            },
          });
        }
        if (Boolean(response?.items)) {
          const cartItems = getItems(response?.items);
          if (explicitItems?.length) {
            explicitItems.forEach(({ id, quantity }) => {
              const isDeleted = quantity === 0;
              // Deleted lines are absent from the response, so publish from the stale list.
              publishItemUpdate(
                id,
                isDeleted ? checkoutItems : cartItems,
                isDeleted ? "delete" : "decrement",
                isCartItem,
              );
            });
          } else {
            publishItemUpdate(item_id, action === "delete" ? checkoutItems : cartItems, action, isCartItem);
          }
          setCheckoutItems(cartItems);
          handleSendItemUpdatedEvent(cartItems);

          // If last item:
          if (!cartItems || cartItems?.length === 0) {
            publishCartUpdate([]);
            handleItemDeleteSubmit(true);
            return;
          }
        }

        setIsLoading(false);
        setIsUpdating?.(false);
        setShowFullScreenLoader(false);

        handleMiscUpdates(response, {
          addType: "ITEM",
        });
      } catch (e: any) {
        if (e?.response?.status === 409) {
          const conflictingItemsRemovalData = extractConflictingItemsData(
            e?.response?.data,
            item_id ?? "",
            variant_id ?? "",
          );
          setCombinedProductRemovalItems(conflictingItemsRemovalData);
          setCheckoutModal("COMBINED_PRODUCT_REMOVE");
          setIsLoading(false);
          setIsUpdating?.(false);
          setShowFullScreenLoader(false);
          return;
        }

        console.error(e);
        setIsLoading(false);
        setIsUpdating?.(false);
        setShowFullScreenLoader(false);
      }
    },
    [
      availableQuantity,
      billing?.shipping,
      checkoutId,
      checkoutView,
      isAuthenticated,
      item_id,
      quantity,
      checkoutItems,
      variant_id,
      merchant?.maxQuantityPerItem,
    ],
  );

  /**
   * Swap the line item onto a different variant of the same product, keeping its quantity.
   * Done as a single PUT (remove old + add new) so the cart never briefly holds both or neither.
   */
  const handleVariantSwap = useCallback(
    async (newVariantId: string, newVariantAvailableQuantity: number) => {
      if (!newVariantId || newVariantId === variant_id || !quantity) return;

      // The old line's quantity carries over, but the target variant may have less stock
      // (or a lower merchant-wide cap) than the line currently holds — clamp it the same way
      // the +/- and manual-entry paths do.
      const maxQuantity = Math.min(
        newVariantAvailableQuantity,
        merchant?.maxQuantityPerItem ? merchant.maxQuantityPerItem : newVariantAvailableQuantity,
      );
      const newQuantity = Math.max(MIN_ITEM_QUANTITY, Math.min(quantity, maxQuantity));

      // Changing variant deletes this line and creates a brand-new one, which
      // drops any item-level add-ons attached to it - capture them so they can
      // be reattached to the new line below, for whichever are still eligible.
      const previousAddOns =
        checkoutItems.find((cartItem) => cartItem.item_id === item_id)?.appliedItemAddOns ?? [];

      const payload = {
        items: [
          { id: item_id, quantity: 0 },
          { variant_id: newVariantId, quantity: newQuantity, new_item: true },
        ],
      };

      try {
        setIsLoading(true);
        setIsUpdating?.(true);

        const response: any = await putCheckoutItems(payload);

        if (Boolean(response?.items)) {
          const cartItems = getItems(response?.items);
          publishItemUpdate(item_id, cartItems, "set", isCartItem);
          setCheckoutItems(cartItems);

          if (previousAddOns.length > 0) {
            const newItem = cartItems.find((cartItem) => cartItem.variant_id === newVariantId);
            const freshAddOns = response?.available_addons ?? {};
            // Reuse the same eligibility rules CheckoutItems/cart use to decide which
            // add-ons to show for a line, so a legacy add-on without an addon_reference_id
            // mapping (eligible on every item, per that helper) doesn't get treated as
            // ineligible here and silently dropped on swap.
            const eligibleAddOnIds = new Set(
              newItem
                ? getItemLevelAddOnsForItemIndex(freshAddOns, newItem.itemIndex ?? -1, cartItems).map(
                    (addon) => addon.addon_id,
                  )
                : [],
            );

            for (const addOn of previousAddOns) {
              const freshAddOnData = freshAddOns[addOn.addon_id];
              const stillEligible = newItem && eligibleAddOnIds.has(addOn.addon_id);

              if (stillEligible) {
                // eslint-disable-next-line no-await-in-loop
                await handleAddOnEdit({
                  action: "ADD",
                  addOnLevel: "ITEM",
                  addOnId: addOn.addon_id,
                  addOnPrice: freshAddOnData.price,
                  itemId: newItem.item_id,
                });
              }
            }
          }
        }

        setIsLoading(false);
        setIsUpdating?.(false);

        handleMiscUpdates(response, { addType: "ITEM" });
      } catch (e: any) {
        if (e?.response?.status === 409) {
          const conflictingItemsRemovalData = extractConflictingItemsData(
            e?.response?.data,
            item_id ?? "",
            variant_id ?? "",
          );
          setCombinedProductRemovalItems(conflictingItemsRemovalData);
          setCheckoutModal("COMBINED_PRODUCT_REMOVE");
          setIsLoading(false);
          setIsUpdating?.(false);
          return;
        }

        console.error(e);
        errorToast((e as any)?.response?.data?.error?.message ?? t("something_went_wrong"));
        setIsLoading(false);
        setIsUpdating?.(false);
      }
    },
    [
      checkoutId,
      isAuthenticated,
      isCartItem,
      item_id,
      merchant?.maxQuantityPerItem,
      quantity,
      variant_id,
      checkoutItems,
    ],
  );

  const handleAddOnEdit = async (args: HandleEditAddOnReturn): Promise<boolean> => {
    try {
      const {
        action,
        addOnLevel,
        addOnId,
        addOnPrice,
        showFullScreenLoader,
        isCartNote,
        cartNotePayload,
        itemId,
      } = args;
      setIsLoading(true);
      setIsUpdating?.(true);

      const payload = {
        addon_id: addOnId,
        item_id: itemId ?? item_id ?? null,
        addon_level: addOnLevel,
      };
      let response;

      const ADDON_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/addon`);
      switch (action) {
        case "ADD": {
          setShowFullScreenLoader(showFullScreenLoader ?? false);
          if (isAuthenticated) {
            response = await postRequest(
              ADDON_PATH,
              isCartNote ? cartNotePayload : payload,
              "KRATOS_PRIVATE",
            );
          } else {
            response = await postRequest(ADDON_PATH, isCartNote ? cartNotePayload : payload, "KRATOS_PUBLIC");
          }
          break;
        }
        case "DELETE": {
          setShowFullScreenLoader(showFullScreenLoader ?? false);
          if (isAuthenticated) {
            response = await deleteRequest(ADDON_PATH, payload, "KRATOS_PRIVATE");
          } else {
            response = await deleteRequest(ADDON_PATH, payload, "KRATOS_PUBLIC");
          }
          break;
        }
      }

      if (Boolean(response?.items)) {
        if (addOnLevel === "ITEM") {
          const cartItems = getItems(response?.items);
          setCheckoutItems(cartItems);
          if (response?.available_addons) {
            setCheckout({ availableAddOns: { ...availableAddOns, ...response.available_addons } });
          }
        } else if (addOnLevel === "CART") {
          setCheckout({
            appliedCartAddOns: response?.cart_addons?.filter((item: AddOnType) => !item?.removed) ?? [],
            availableAddOns: response?.available_addons
              ? { ...availableAddOns, ...response.available_addons }
              : availableAddOns,
          });
        }
        const parsedBilling = getBillingDetails(response?.pricing);
        setBilling(parsedBilling);
      }

      if (action === "ADD") {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_ADDON_ADDED,
          eventType: "click",
          eventFor: [analyticsTypes.FACEBOOK_PIXEL, analyticsTypes.SF_ANALYTICS],
          metaData: {
            checkoutItems: getItems(response?.items),
            addOnData: {
              addOnType: addOnLevel,
              addOnId: addOnId,
              itemId: variant_id,
              price: addOnPrice ? addOnPrice * 100 : 0,
            },
          },
        });
      } else {
        handleSendDeleteAnalyticsEvent({
          addOnType: addOnLevel,
          addOnId: addOnId,
          price: addOnPrice ? addOnPrice * 100 : 0,
          itemId: variant_id,
        });
      }

      setIsLoading(false);
      setIsUpdating?.(false);
      setShowFullScreenLoader(false);

      handleMiscUpdates(response, {
        addType: "ADDON",
      });

      return true;
    } catch (e) {
      const errorMsg = (e as any)?.response?.data?.error?.message;
      errorToast(errorMsg ?? t("something_went_wrong"));
      setCheckoutItems([...checkoutItems]);
      setIsLoading(false);
      setIsUpdating?.(false);
      setShowFullScreenLoader(false);

      return false;
    }
  };

  const handleAddItemWithResult = async (
    items: Array<{ variantId: string; quantity: number }>,
    openCart: boolean = true,
  ): Promise<boolean> => {
    const payload = {
      items: items.map(({ variantId, quantity }) => ({ variant_id: variantId, quantity, new_item: true })),
    };

    try {
      setIsLoading(true);
      setIsUpdating?.(true);
      const response: any = await putCheckoutItems(payload);

      if (Boolean(response?.items)) {
        const cartItems = getItems(response?.items);
        setCheckoutItems(cartItems);
      }

      if (openCart) {
        items.forEach(({ variantId, quantity }) => {
          publishPostMessage(eventTypes.ADD_TO_CART, { item: { id: variantId, quantity } });
        });
      }

      setIsLoading(false);
      setIsUpdating?.(false);
      handleMiscUpdates(response, { addType: "ITEM" });
      return true;
    } catch (e) {
      console.error(e);
      setIsLoading(false);
      setIsUpdating?.(false);
      return false;
    }
  };

  /**
   * A single variant can have more than one line item in the checkout, eg. a BXGY
   * coupon splits a variant into a discounted line item and a regular one. Such
   * lines are flagged as freebies or discount alterations by the backend.
   */
  const isDiscountedLineItem = (item: ItemType) =>
    Boolean(item?.is_freebie) || Boolean(item?.is_discount_alteration);

  const resolveLineItem = ({ variantId, index }: RemoveItemInput): ItemType | undefined => {
    if (typeof index === "number") return checkoutItems?.[index];
    if (!Boolean(variantId)) return undefined;
    const matchingItems = checkoutItems?.filter((item) => String(item?.variant_id) === String(variantId));
    if (!Boolean(matchingItems?.length)) return undefined;
    // Prefer the non-discounted line item when a variant maps to multiple lines.
    return matchingItems.find((item) => !isDiscountedLineItem(item)) ?? matchingItems[0];
  };

  const handleRemoveItem = async (items: RemoveItemInput[]): Promise<void> => {
    if (!Boolean(items?.length)) return;

    // Multiple inputs can resolve to the same line item, so track the remaining
    // quantity per line item and send a single update.
    const remainingQuantities = new Map<string, number>();
    items.forEach(({ variantId, index, quantity }) => {
      const lineItem = resolveLineItem({ variantId, index });
      if (!lineItem) return;
      const currentQuantity = remainingQuantities.get(lineItem.item_id) ?? lineItem.quantity;
      // Remove the whole line item when no quantity is passed.
      const quantityToRemove = quantity === undefined || quantity === null ? currentQuantity : quantity;
      if (quantityToRemove <= 0) return;
      remainingQuantities.set(lineItem.item_id, Math.max(currentQuantity - quantityToRemove, 0));
    });

    if (!Boolean(remainingQuantities.size)) return;

    const lineItems = Array.from(remainingQuantities, ([id, quantity]) => ({ id, quantity }));
    const action: ItemEditAction = lineItems.every(({ quantity }) => quantity === 0) ? "delete" : "decrement";

    await handleItemEdit(action, { items: lineItems });
  };

  const handleAddItem = async (
    items: Array<{ variantId: string; quantity: number }>,
    openCart: boolean = true,
  ): Promise<void> => {
    await handleAddItemWithResult(items, openCart);
  };

  return {
    handleItemEdit,
    isLoading,
    showFullScreenLoader,
    openRemoveItemDialog,
    setOpenRemoveItemDialog,
    handleItemDeleteSubmit,
    handleAddOnEdit,
    handleVariantSwap,
    handleAddItem,
    handleAddItemWithResult,
    handleRemoveItem,
  };
};
